import { BASE_URL } from "@/dashboardQueries";
import { AccreditationGroup } from "@/models/accreditation";
import { Email, EmailThread } from "@/models/email";
import axios from "axios";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useInfiniteQuery } from "react-query";
import { isToday, format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronUp,
  CornerUpLeft,
  Mail,
  Reply,
} from "lucide-react";
import clsx from "clsx";
import { OpenClickCounts, ViewEmail } from "./ViewEmail";
import { useAccreditationStore } from "@/store/accreditation";

/*
todo list for emails:
- variables for individuals
- attachments
- notifications
- highlighting when it is unread
- sending entire thread in outgoing?
*/

interface Props {
  group: AccreditationGroup;
}

// Utility to strip HTML tags and get plain text
function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

export const ViewGroupEmails = ({ group }: Props) => {
  const [page, setPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState<EmailThread | null>(null);
  const { replyToEmailOpen, setReplyToEmailOpen } = useAccreditationStore();

  const fetchEmails = async ({ pageParam = 1 }) => {
    const response = await axios.get(
      `${BASE_URL}/accreditation/groups/${group.id}/email-threads?page=${pageParam}`
    );
    return response.data;
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery(
    ["groupEmails", 1], // The ID '1' is hardcoded for this example
    ({ pageParam = 1 }) => fetchEmails(pageParam),
    {
      getNextPageParam: (lastPage) => {
        // Return the next page number if there are more pages
        if (lastPage.hasNext) {
          return lastPage.page + 1;
        }
        // Return undefined to signal that there are no more pages
        return undefined;
      },
    }
  );

  const allEmails = data?.pages.flatMap((page) => page.results) ?? [];

  // Intersection observer for infinite scroll
  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 });

  // Fetch the next page if we have a next page, its in view and we aren't fetching already
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="w-full border-t">
      <div className="flex w-full h-screen">
        <div className="w-1/4">
          {data && (
            <div className="px-4 border-b dark:border-white/10 flex items-center gap-x-3 h-12">
              <h3 className="">Inbox</h3>
              <p className="text-sm">{data.pages[0].count} messages</p>
            </div>
          )}
          <div>
            <div>
              <ul
                role="list"
                className={clsx(
                  "divide-y dark:divide-white/10",
                  allEmails.length > 0 && "border-b dark:border-white/10"
                )}
              >
                {allEmails.map((email: EmailThread) => {
                  const plainText = stripHtml(email.latestEmail.body);
                  const isTruncated = plainText.length > 75;
                  const displayText = isTruncated
                    ? plainText.slice(0, 75) + "..."
                    : plainText;

                  // Parse the date string to a Date object
                  const dateObj = parseISO(email.latestEmail.createdAt);
                  const dateDisplay = isToday(dateObj)
                    ? format(dateObj, "HH:mm")
                    : format(dateObj, "d MMM");

                  return (
                    <li
                      key={email.id}
                      onClick={() => setSelectedEmail(email)}
                      className={clsx(
                        "cursor-pointer relative px-6 py-5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-600 hover:bg-gray-50 dark:hover:bg-white/5 transition-all duration-300",
                        selectedEmail?.id == email.id ? "bg-zinc-800" : ""
                      )}
                    >
                      <div className="flex justify-between space-x-3">
                        <div className="min-w-0 flex-1">
                          <a
                            // href={message.href}
                            className="block focus:outline-none"
                          >
                            <span
                              aria-hidden="true"
                              className="absolute inset-0"
                            />
                            <p className="truncate text-sm font-medium text-zinc-400">
                              {email.latestEmail.type === "Inbound"
                                ? email.latestEmail.fromEmail
                                : email.latestEmail.to}
                            </p>
                            <p className="truncate text-sm text-zinc-500">
                              {email.subject}
                            </p>
                          </a>
                          {email.latestEmail.type == "Outbound" && (
                            <OpenClickCounts email={email.latestEmail} />
                          )}
                        </div>
                        <time
                          dateTime={email.createdAt}
                          className="shrink-0 whitespace-nowrap text-sm text-zinc-500"
                        >
                          {dateDisplay}
                        </time>
                      </div>
                      <div className="mt-1">
                        <p className="line-clamp-2 text-sm text-gray-600">
                          {displayText}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            {/* Infinite scroll trigger */}
            {hasNextPage && (
              <div ref={loadMoreRef} className="py-4 flex justify-center">
                {isFetchingNextPage ? (
                  <span className="text-gray-400 text-sm">Loading more...</span>
                ) : (
                  <span className="text-gray-400 text-sm">
                    Scroll to load more...
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="w-3/4 border-l dark:border-white/10 flex flex-col h-screen">
          {selectedEmail ? (
            <>
              <div className="border-b dark:border-white/10 px-6 h-12 flex items-center justify-between shrink-0">
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() => setReplyToEmailOpen(true)}
                >
                  <CornerUpLeft className="size-4 mr-1" />
                  Reply
                </Button>
                <div>
                  <Button variant="secondary" size="xs" className="mr-1">
                    <ChevronUp className="size-5" />
                  </Button>
                  <Button variant="secondary" size="xs">
                    <ChevronDown className="size-5" />
                  </Button>
                </div>
              </div>
              <div className="border-b dark:border-white/10 px-6 py-4 shrink-0">
                <h1
                  id="message-heading"
                  className="text-lg font-medium text-zinc-400"
                >
                  RE: {selectedEmail.subject}
                </h1>
              </div>
              <div className="flex-1 overflow-y-auto">
                <ViewEmail group={group} thread={selectedEmail} />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full -mt-32">
              <div className="text-center">
                <div className="flex items-center justify-center">
                  <Mail className="size-7" />
                </div>
                <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  No conversation selected
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Selected a conversation on the left hand side to view it
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
